import type { PointerInputEvent, SuppressionLevel } from "../core/types.ts";
import { FrameClock, type Mechanic, type Readout } from "./mechanic.ts";

/**
 * Suppression as *effort* — the axis none of the other mechanics occupy.
 *
 * A marker wanders off centre and the thumb pushes it back. Suppression does
 * not take anything away from you; it makes the wandering faster, so holding
 * the line costs more taps per second. You can fight it with thumb work alone
 * for a while, and then you cannot. That is the honest shape of the thing the
 * product is about: not blinking is not instantly punished, it just quietly
 * raises the price of everything else you are doing.
 *
 * It is also the first mechanic to use where you touched rather than that you
 * touched — press on the side you want to push toward. `PointerInputEvent`
 * has carried normalised x/y since the channel was built and nothing had
 * consumed it.
 *
 * §2 is satisfied properly here, unlike Blackout: the thumb is genuinely the
 * high-frequency verb and blinking never enters the control loop at all. Blink
 * naturally and the drift stays slow enough that you never think about it.
 */

/** Track units per second at rest. The track runs -1 (left) to +1 (right). */
const BASE_DRIFT = 0.42;

/**
 * How much harder full suppression makes it — bounded by what a thumb can do.
 *
 * The pressure has to stay winnable or it stops being pressure. Required tap
 * rate is `BASE_DRIFT × (0.35 + gain) / NUDGE`, and measured tap gaps from real
 * sessions are p10 168ms, p50 250ms — so a sustained burst is about four
 * presses a second and anything past that is fantasy.
 *
 * The first version used 1.7, which demands **6.6 presses a second** at full
 * suppression: not hard, impossible, the same mistake tempo made with a 63ms
 * aiming window. 0.6 puts the ceiling at 3.1/s against a resting 1.1/s — the
 * price of not blinking roughly triples, and every point on that curve is
 * something a thumb can actually pay.
 */
const SUPPRESSION_GAIN = 0.6;

/** How far one press moves the marker. */
const NUDGE = 0.13;

/** Beyond this the marker is about to be lost, and the readout says so. */
const DANGER = 0.7;

/** Fraction of the track the marker occupies. */
const MARKER_SPAN = 9;

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

/**
 * The wandering force — deterministic, and deliberately not random.
 *
 * Two incommensurable periods read as unpredictable over a 90-second session
 * while remaining a pure function of time, so a test can drive the mechanic to
 * an exact position and a replay produces the same run twice.
 */
function drift(t: number): number {
  return Math.sin(t / 1700) * 0.62 + Math.sin(t / 610) * 0.38;
}

export class Balance implements Mechanic {
  readonly id = "balance" as const;
  readonly hint = "tap the side you want to push toward · blink to steady it";

  #clock = new FrameClock();
  #position = 0;
  #suppression = 0;
  #falls = 0;
  #elapsed = 0;

  constructor(private readonly marker: HTMLElement) {}

  onSuppression(level: SuppressionLevel): void {
    this.#suppression = level.value;
  }

  onPointer(event: PointerInputEvent): void {
    if (event.phase !== "down") return;
    // Left half pushes left, right half pushes right. Applied immediately
    // rather than queued: this is a correction, and a correction that waits a
    // frame to be applied is a correction you make twice.
    this.#position = clamp(this.#position + (event.x < 0.5 ? -NUDGE : NUDGE), -1, 1);
  }

  update(t: number): void {
    const dt = this.#clock.step(t);
    this.#elapsed += dt;

    const speed = BASE_DRIFT * (0.35 + this.#suppression * SUPPRESSION_GAIN);
    this.#position += drift(this.#elapsed) * speed * (dt / 1000);

    if (Math.abs(this.#position) >= 1) {
      this.#falls++;
      this.#position = 0;
    }

    const left = ((this.#position + 1) / 2) * (100 - MARKER_SPAN);
    this.marker.style.left = `${left.toFixed(2)}%`;
    // Reddens as it approaches the edge, so the warning arrives through
    // peripheral vision rather than by reading a number.
    const strain = clamp((Math.abs(this.#position) - 0.35) / 0.65, 0, 1);
    this.marker.style.background = `hsl(${(140 - strain * 140).toFixed(0)}, 80%, 60%)`;
  }

  readout(): Readout {
    const danger = Math.abs(this.#position) > DANGER;
    return {
      value: String(this.#falls),
      label: "falls",
      ...(danger ? { alert: true } : {}),
    };
  }

  get position(): number {
    return this.#position;
  }

  get falls(): number {
    return this.#falls;
  }

  reset(): void {
    this.#clock.reset();
    this.#position = 0;
    this.#suppression = 0;
    this.#falls = 0;
    this.#elapsed = 0;
    this.marker.style.left = "";
    this.marker.style.background = "";
  }
}

export { BASE_DRIFT, NUDGE, SUPPRESSION_GAIN };
